// Quote completion % (bigger, next to Save) + "Fix" pop-up listing every missing
// field with a quick editor, a jump-to button, or "Ignore" (hidden, still counted as empty).
import { useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { ListChecks, X, CornerDownRight, EyeOff, Undo2, Sparkles, Check } from "lucide-react";
import api from "../../../lib/api";
import VoiceNoteButton from "./VoiceNoteButton";
import { quoteCompletion, completionTone } from "../_quoteHelpers";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../components/ui/tooltip";

// label → quick editor ({ get, set, type }) or jump target (testid of the field on the page)
const EDIT = {
  "Project name / postcode": { type: "text", ph: "e.g. Smith loft · BS7 8NN", get: (q) => q.ProjectRef, set: (q, v) => ({ ProjectRef: v }) },
  "Mobile": { type: "tel", get: (q) => q.Customer?.phone, set: (q, v) => ({ Customer: { ...q.Customer, phone: v } }) },
  "Delivery address / postcode": { type: "text", ph: "Postcode", get: (q) => q.Delivery?.deliveryAddress?.postcode,
    set: (q, v) => ({ Delivery: { ...q.Delivery, addressSource: q.Delivery?.addressSource === "invoice" || !q.Delivery?.addressSource ? "manual" : q.Delivery.addressSource, deliveryAddress: { ...(q.Delivery?.deliveryAddress || {}), postcode: v.toUpperCase() } } }) },
  "Target date (or ASAP)": { type: "date", get: (q) => (q.Timeline?.targetDate || "").slice(0, 10), set: (q, v) => ({ Timeline: { ...q.Timeline, targetDate: v || null } }) },
  "Delivery window": { type: "text", ph: "e.g. Fri AM before 11am", get: (q) => q.Delivery?.deliveryWindow, set: (q, v) => ({ Delivery: { ...q.Delivery, deliveryWindow: v } }) },
  "Site contact": { type: "text", ph: "Name on site", get: (q) => q.Delivery?.siteContactName, set: (q, v) => ({ Delivery: { ...q.Delivery, siteContactName: v } }) },
  "Access": { type: "text", ph: "Kerbside / yard / narrow lane…", get: (q) => q.Delivery?.access, set: (q, v) => ({ Delivery: { ...q.Delivery, access: v } }) },
  "Parking": { type: "text", ph: "Yellow lines / street / none", get: (q) => q.Delivery?.parking, set: (q, v) => ({ Delivery: { ...q.Delivery, parking: v } }) },
  "Weight estimate": { type: "number", ph: "kg", get: (q) => q.Delivery?.estimatedWeightKg, set: (q, v) => ({ Delivery: { ...q.Delivery, estimatedWeightKg: v ? Number(v) : null } }) },
  "Customer site notes": { type: "textarea", ph: "What the customer told us about their site…", get: (q) => q.Delivery?.notes, set: (q, v) => ({ Delivery: { ...q.Delivery, notes: v }, CustomerFacingNotes: v }) },
};
const JUMP = {
  "Customer": "panel-customer", "Build type": "site-build-block", "Urgency": "urgency-desperation",
  "Delivery method": "btn-carriage", "On-site facts asked": "panel-customer-site-notes",
};

function SuggestBox({ slug, value, onAccept, onSkip }) {
  return (
    <div className="qb-ai-sugg" data-testid={`fix-sugg-${slug}`}
      style={{ display: "flex", alignItems: "center", gap: 8, margin: "6px 0", padding: "6px 9px", borderRadius: 8, border: "1px dashed rgba(167,139,250,.7)", background: "rgba(139,92,246,.12)" }}>
      <Sparkles className="w-3.5 h-3.5" style={{ color: "#c4b5fd", flexShrink: 0 }} />
      <span style={{ flex: 1, fontSize: 12, color: "#ede9fe" }}><b style={{ fontSize: 9.5, letterSpacing: ".08em", color: "#c4b5fd" }}>AI SUGGESTION · </b><span data-testid={`fix-sugg-value-${slug}`}>{value}</span></span>
      <button type="button" className="t2-btn-ghost" style={{ padding: "3px 9px", fontSize: 10.5, color: "#6ee7b7" }} onClick={onAccept} data-testid={`fix-sugg-accept-${slug}`}><Check className="w-3 h-3" /> Accept</button>
      <button type="button" className="t2-btn-ghost" style={{ padding: "3px 9px", fontSize: 10.5 }} onClick={onSkip} data-testid={`fix-sugg-skip-${slug}`}>Skip</button>
    </div>
  );
}

function FixRow({ label, quote, onPatch, onIgnore, onClose, sugg, onSuggDone, ignored }) {
  const ed = EDIT[label];
  const [v, setV] = useState(ed ? (ed.get(quote) ?? "") : "");
  const slug = label.toLowerCase().replace(/[^a-z]+/g, "-");
  const acceptSugg = () => {
    setV(sugg);
    onPatch({ ...ed.set(quote, sugg), ...(ignored ? { CompletionIgnored: (quote.CompletionIgnored || []).filter((x) => x !== label) } : {}) });
    onSuggDone();
  };
  const jump = () => {
    onClose();
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="${JUMP[label]}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      if (label === "Delivery method") el?.click();
    }, 80);
  };
  return (
    <div style={{ padding: "10px 0", borderBottom: "1px solid var(--t2-border)" }} data-testid={`fix-row-${slug}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: ed ? 6 : 0 }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--t2-white)", flex: 1 }}>{label}</span>
        {!ed && JUMP[label] && (
          <button type="button" className="t2-btn-ghost" style={{ padding: "3px 9px", fontSize: 10.5 }} onClick={jump} data-testid={`fix-jump-${slug}`}>
            <CornerDownRight className="w-3 h-3" /> Go to field
          </button>
        )}
        {ignored ? <span className="t2-mono" style={{ fontSize: 9.5, color: "var(--t2-grey3)" }}>IGNORED</span> : (
          <button type="button" className="t2-btn-ghost" style={{ padding: "3px 9px", fontSize: 10.5 }} onClick={onIgnore} data-testid={`fix-ignore-${slug}`}
            title="Hide from this list — still counts as empty in the %">
            <EyeOff className="w-3 h-3" /> Ignore
          </button>
        )}
      </div>
      {ed && sugg != null && <SuggestBox slug={slug} value={sugg} onAccept={acceptSugg} onSkip={onSuggDone} />}
      {ed && (ed.type === "textarea" ? (
        <textarea className="t2-input" rows={2} value={v} placeholder={ed.ph} onChange={(e) => setV(e.target.value)} onBlur={() => onPatch(ed.set(quote, v))} data-testid={`fix-input-${slug}`} />
      ) : (
        <input className="t2-input" type={ed.type} style={ed.type === "date" ? { colorScheme: "dark" } : undefined} value={v} placeholder={ed.ph} onChange={(e) => setV(e.target.value)} onBlur={() => onPatch(ed.set(quote, v))} data-testid={`fix-input-${slug}`} />
      ))}
    </div>
  );
}

export default function CompletionPill({ quote, onPatch, billing }) {
  const [open, setOpen] = useState(false);
  const c = quoteCompletion(quote, undefined, billing);
  const cp = `var(--t2-${completionTone(c.pct)}-ink)`;
  const ignore = (k) => onPatch?.({ CompletionIgnored: [...new Set([...(quote.CompletionIgnored || []), k])] });
  const unignore = (k) => onPatch?.({ CompletionIgnored: (quote.CompletionIgnored || []).filter((x) => x !== k) });
  const [sugg, setSugg] = useState({});
  const doneSugg = (k) => setSugg((s) => { const n = { ...s }; delete n[k]; return n; });
  const onVoice = async ({ raw, clean }) => {
    const fields = [...c.missing, ...c.ignored].filter((k) => EDIT[k]).map((k) => ({ label: k, type: EDIT[k].type, hint: EDIT[k].ph || "" }));
    if (!fields.length) { toast.message("No typed fields left to fill — use Go to field for the rest"); return; }
    try {
      const { data } = await api.post(`/quotes/${quote._id}/site-fill`, { text: raw || clean, fields }, { timeout: 90000 });
      const n = Object.keys(data.suggestions || {}).length;
      setSugg(data.suggestions || {});
      toast[n ? "success" : "message"](n ? `${n} suggestion${n === 1 ? "" : "s"} — accept each one you agree with` : "Didn't catch any site details — try again");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI suggestions failed"); }
  };
  return (
    <>
      <TooltipProvider delayDuration={120}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" className="qb-complete-pill qb-complete-lg" style={{ "--qb-cp": cp, "--qb-p": c.pct }} data-testid="quote-completion-pill"
              onClick={() => onPatch && setOpen(true)}>
              <span className="qb-complete-ring" />
              <span data-testid="quote-completion-pct" style={{ fontSize: 17 }}>{c.pct}%</span>
              {c.missing.length > 0 && onPatch && <span className="qb-complete-fix" data-testid="quote-completion-fix"><ListChecks className="w-3.5 h-3.5" /> Fix {c.missing.length}</span>}
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="end" className="qb-complete-tip max-w-[300px] p-3" data-testid="quote-completion-tooltip">
            <div className="font-semibold text-[12.5px] mb-1">Quote completion · {c.filled}/{c.total}</div>
            {c.missing.length ? (
              <>
                <div className="text-[11.5px] mb-1.5 qb-tip-sub">Click to fill the gaps in one place — ask the customer for anything you don't know.</div>
                <ul className="text-[11px] list-disc pl-4">{c.missing.map((m) => <li key={m}>{m}</li>)}</ul>
              </>
            ) : <div className="text-[11.5px]">Everything captured — great job.</div>}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {open && createPortal(
        <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setOpen(false)} data-testid="completion-fix-modal">
          <div className="t2-panel" style={{ width: "100%", maxWidth: 520, maxHeight: "86vh", overflowY: "auto", padding: 18 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <span className="t2-mono" style={{ fontSize: 26, fontWeight: 900, color: cp }}>{c.pct}%</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: "var(--t2-white)" }}>Complete this quote</div>
                <div style={{ fontSize: 11, color: "var(--t2-grey3)" }}>{c.missing.length ? `${c.missing.length} thing${c.missing.length === 1 ? "" : "s"} left — fill, jump to, or ignore. Press Save when done.` : c.ignored.length ? "Nothing left to fix — ignored items still count as empty." : "All done — press Save."}</div>
              </div>
              <VoiceNoteButton url="/ai/transcribe" onResult={onVoice} testid="completion-voice" label="Speak details"
                title="Say the site details (window, access, parking, weight…) — AI suggests a value per field for you to accept" />
              <button type="button" onClick={() => setOpen(false)} style={{ background: "none", border: "none", color: "var(--t2-grey3)", cursor: "pointer" }} data-testid="completion-fix-close" aria-label="Close"><X className="w-4 h-4" /></button>
            </div>
            {c.missing.map((k) => (
              <FixRow key={k} label={k} quote={quote} onPatch={onPatch} onIgnore={() => ignore(k)} onClose={() => setOpen(false)} sugg={sugg[k]} onSuggDone={() => doneSugg(k)} />
            ))}
            {c.ignored.filter((k) => sugg[k] != null && EDIT[k]).map((k) => (
              <FixRow key={`ig-${k}`} label={k} quote={quote} onPatch={onPatch} onClose={() => setOpen(false)} sugg={sugg[k]} onSuggDone={() => doneSugg(k)} ignored />
            ))}
            {c.ignored.length > 0 && (
              <div style={{ marginTop: 12 }} data-testid="completion-ignored-list">
                <div className="t2-mono" style={{ fontSize: 9.5, letterSpacing: ".12em", color: "var(--t2-grey3)", marginBottom: 4 }}>IGNORED · STILL COUNTED AS EMPTY</div>
                {c.ignored.map((k) => (
                  <div key={k} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 11.5, color: "var(--t2-grey2)", padding: "3px 0" }}>
                    <span>{k}</span>
                    <button type="button" onClick={() => unignore(k)} className="t2-mono" data-testid={`fix-unignore-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      style={{ background: "none", border: "none", color: "var(--t2-teal-ink)", cursor: "pointer", fontSize: 10, display: "inline-flex", alignItems: "center", gap: 3 }}>
                      <Undo2 className="w-3 h-3" /> Undo
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
              <button type="button" className="t2-btn-primary" onClick={() => setOpen(false)} data-testid="completion-fix-done">Done</button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
